
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../App.jsx';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { CHATS_COLLECTION } from '../../config/constants';
import AuthLoadingScreen from '../AuthLoadingScreen.jsx';
import { ArrowLeft, ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, MapPin, MessageSquare, AlertCircle } from 'lucide-react';

const AgendaScreen = () => {
    const { db, user, showMessage } = useAppContext();
    const navigate = useNavigate();
    const [appointments, setAppointments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [viewDate, setViewDate] = useState(new Date()); // Data usada para navegar no calendário (mes/ano)

    useEffect(() => {
        if (!db || !user?.uid) return;

        // Busca todos os chats com status "Agendado" para este profissional
        const q = query(
            collection(db, CHATS_COLLECTION),
            where("serviId", "==", user.uid),
            where("status", "==", "Agendado"),
            orderBy("scheduledAt", "asc")
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                scheduledAt: doc.data().scheduledAt?.toDate() || null
            })).filter(a => a.scheduledAt !== null);

            setAppointments(data);
            setIsLoading(false);
        }, (error) => {
            console.error("Erro ao carregar agenda:", error);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [db, user?.uid]);

    // Lógica do Calendário
    const calendarDays = useMemo(() => {
        const year = viewDate.getFullYear();
        const month = viewDate.getMonth();
        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const days = [];
        // Espaços vazios do mês anterior
        for (let i = 0; i < firstDay; i++) days.push(null);
        // Dias do mês atual
        for (let i = 1; i <= daysInMonth; i++) days.push(new Date(year, month, i));

        return days;
    }, [viewDate]);

    const isSameDay = (d1, d2) =>
        d1 && d2 && d1.getDate() === d2.getDate() && d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear();

    const appointmentsForSelectedDay = useMemo(() =>
        appointments.filter(a => isSameDay(a.scheduledAt, selectedDate))
    , [appointments, selectedDate]);

    const hasAppointmentOnDay = (date) =>
        appointments.some(a => isSameDay(a.scheduledAt, date));

    const nextMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
    const prevMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));

    if (isLoading) return <AuthLoadingScreen message="Sincronizando sua agenda..." />;

    return (
        <div className="p-4 h-full flex flex-col bg-gray-50 text-left">
            <header className="flex items-center mb-6">
                <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-200 mr-2 transition-all active:scale-90">
                    <ArrowLeft size={24} className="text-gray-700" />
                </button>
                <h1 className="text-2xl font-bold text-gray-800">Minha Agenda</h1>
            </header>

            {/* CALENDÁRIO VISUAL */}
            <div className="bg-white rounded-[2rem] shadow-xl border border-gray-100 p-5 mb-6 animate-fade-in">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="font-black text-sm uppercase tracking-[0.2em] text-blue-600">
                        {viewDate.toLocaleString('pt-BR', { month: 'long', year: 'numeric' })}
                    </h2>
                    <div className="flex space-x-1">
                        <button onClick={prevMonth} className="p-2 hover:bg-gray-100 rounded-xl"><ChevronLeft size={20}/></button>
                        <button onClick={nextMonth} className="p-2 hover:bg-gray-100 rounded-xl"><ChevronRight size={20}/></button>
                    </div>
                </div>

                <div className="grid grid-cols-7 gap-1 text-center mb-2">
                    {['D','S','T','Q','Q','S','S'].map((d, i) => (
                        <span key={i} className="text-[10px] font-black text-gray-300 uppercase">{d}</span>
                    ))}
                </div>

                <div className="grid grid-cols-7 gap-1">
                    {calendarDays.map((date, i) => {
                        if (!date) return <div key={`empty-${i}`} className="aspect-square"></div>;
                        const active = isSameDay(date, selectedDate);
                        const hasJob = hasAppointmentOnDay(date);
                        const isToday = isSameDay(date, new Date());

                        return (
                            <button
                                key={i}
                                onClick={() => setSelectedDate(date)}
                                className={`relative aspect-square flex flex-col items-center justify-center rounded-2xl text-sm font-bold transition-all active:scale-90
                                    ${active ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' : 'hover:bg-gray-50 text-gray-700'}
                                    ${isToday && !active ? 'border-2 border-blue-100' : ''}
                                `}
                            >
                                {date.getDate()}
                                {hasJob && (
                                    <span className={`absolute bottom-2 w-1 h-1 rounded-full ${active ? 'bg-white' : 'bg-blue-500 animate-pulse'}`}></span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* LISTA DE SERVIÇOS DO DIA SELECIONADO */}
            <div className="flex-grow overflow-y-auto space-y-4 pb-20">
                <div className="flex items-center justify-between px-2 mb-2">
                    <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest flex items-center">
                        <Clock className="w-3.5 h-3.5 mr-2" />
                        Compromissos: {selectedDate.toLocaleDateString('pt-BR')}
                    </h3>
                    <span className="text-[10px] font-black bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">
                        {appointmentsForSelectedDay.length} {appointmentsForSelectedDay.length === 1 ? 'SERVIÇO' : 'SERVIÇOS'}
                    </span>
                </div>

                {appointmentsForSelectedDay.length > 0 ? (
                    appointmentsForSelectedDay.map(appt => (
                        <div key={appt.id} className="bg-white p-5 rounded-[2rem] shadow-md border border-gray-100 animate-slide-up group">
                            <div className="flex justify-between items-start mb-4">
                                <div className="flex items-center">
                                    <div className="bg-blue-50 p-2.5 rounded-2xl mr-3">
                                        <Clock className="text-blue-600 w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="text-lg font-black text-gray-800 leading-none">
                                            {appt.scheduledAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                        </p>
                                        <p className="text-[10px] text-gray-400 font-bold uppercase mt-1">Horário Previsto</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => navigate(`/servi/chat/${appt.id}`)}
                                    className="p-3 bg-gray-50 text-gray-400 rounded-2xl hover:bg-blue-50 hover:text-blue-600 transition-all"
                                >
                                    <MessageSquare size={20} />
                                </button>
                            </div>

                            <h4 className="text-sm font-bold text-gray-800 mb-2 px-1">{appt.serviceTitle}</h4>

                            <div className="flex items-center text-xs text-gray-500 bg-gray-50 p-3 rounded-2xl">
                                <MapPin size={14} className="mr-2 text-red-400" />
                                <span className="font-medium truncate">
                                    {appt.participantDetails[appt.clientId]?.name || 'Cliente T-SERVI'}
                                </span>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="text-center py-16 bg-white rounded-[2.5rem] border-2 border-dashed border-gray-100">
                        <CalendarIcon className="w-12 h-12 text-gray-100 mx-auto mb-3" />
                        <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Agenda livre para este dia</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AgendaScreen;
